import { SxProps, styled } from '@mui/material/styles';
import { ButtonBase, ButtonBaseProps, Theme } from '@mui/material';
import { StyledComponent } from '@emotion/styled';

export type CoreButtonVariant = 'primary' | 'secondary' | 'danger' | 'info' | 'info-light' | 'base';

export interface CoreButtonBaseProps extends ButtonBaseProps {
	variant?: CoreButtonVariant;
	contraModeStyleButton?: boolean;
}

const getTextColor = (variant: CoreButtonVariant, theme: Theme) => {
	switch (variant) {
		case 'danger':
			return theme.colors.red;
		case 'primary':
			return theme.colors.orangeDeep;
		case 'secondary':
			return theme.colors.darkGray;
		case 'info':
			return theme.colors.blue;
		case 'info-light':
			return theme.colors.lightBlue;
		case 'base':
			return theme.colors.lightBlue;
	}
};

const getFocusColor = (variant: CoreButtonVariant, theme: Theme) => {
	switch (variant) {
		case 'danger':
			return theme.colors.redPressed;
		case 'primary':
			return '#E05920';
		case 'secondary':
			return theme.colors.black;
		case 'info':
		case 'info-light':
			return theme.colors.bluePressed;

		case 'base':
			return theme.colors.white;
	}
};

export const CoreButtonBase: StyledComponent<CoreButtonBaseProps> = styled(ButtonBase)<CoreButtonBaseProps>(
	({ theme, variant = 'primary', contraModeStyleButton = false }) => {
		const { typography, colors, transitions } = theme;

		const textColor = getTextColor(variant, theme);
		const focusColor = getFocusColor(variant, theme);

		return {
			...typography.button,
			textTransform: 'capitalize',
			lineHeight: '24px',
			color: contraModeStyleButton ? colors.white : textColor,
			backgroundColor: contraModeStyleButton ? textColor : colors.white,
			border: `1px solid ${textColor}`,
			borderRadius: '100px',
			padding: '11px 24px',
			transition: transitions.create(['background-color', 'color', 'border-color'], {
				duration: transitions.duration.short,
			}),
			'&:hover, &.Mui-focusVisible, &.core-button-loading': {
				color: contraModeStyleButton ? textColor : colors.white,
				backgroundColor: variant === 'base' ? colors.grayLight : contraModeStyleButton ? colors.white : textColor,
				border: variant === 'base' ? `2px solid ${textColor}` : `1px solid ${textColor}`,
			},
			'&:active': {
				color: contraModeStyleButton ? textColor : colors.white,
				backgroundColor: variant === 'base' ? colors.grayLight : contraModeStyleButton ? colors.white : textColor,
				borderColor: focusColor,
				border: variant === 'base' ? `2px solid ${textColor}` : `1px solid ${textColor}`,
			},
			'&.Mui-focusVisible': {
				boxShadow: `inset 0 0 0 1px ${focusColor}`,
				borderColor: focusColor,
			},
			'&:disabled:not(.core-button-loading)': {
				color: contraModeStyleButton ? colors.white : colors.blueTransparent,
				borderColor: contraModeStyleButton ? colors.white : colors.blueTransparent,
				opacity: contraModeStyleButton ? 0.6 : 1,
			},
		};
	}
);
